import { useId, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { ApiError, demoApi, problemFieldErrors, problemMessage } from "@/api/client";
import { grainBinLabel } from "@/lib/grainBinLabel";
import type { CreateGrainLoadRequest, GrainBin, GrainUnit, Guid } from "@/api/types";
import { DialogShell } from "./DialogShell";
import "./GrainLoadsPanel.css";

/** One planting the form offers: the field-crop id (the option value) and "{field} — {crop}" (the option text). */
export interface PlantingOption {
  id: Guid;
  label: string;
}

/** The units a load can be weighed in, as the wire spells them (lowercase, case-sensitive) and a person reads them. */
export const GRAIN_UNIT_OPTIONS: { value: GrainUnit; label: string }[] = [
  { value: "bu", label: "Bushels" },
  { value: "ton", label: "Tons" },
  { value: "cwt", label: "CWT" },
];

/** What a person sees for a unit in the list - the short form. */
export const GRAIN_UNIT_SHORT: Record<GrainUnit, string> = { bu: "bu", ton: "tons", cwt: "cwt" };

/** `yyyy-MM-ddTHH:mm` of now in the machine's local time - what a `datetime-local` input takes and gives back. */
function localStampNow(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** A form string as a number, or null when it is blank or not a number (never `Number("") === 0`). */
function toNumber(raw: string): number | null {
  if (raw.trim() === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/** More than two decimals cannot be stored (a rounded net would no longer equal gross - tare). */
function hasMoreThanTwoDecimals(n: number): boolean {
  return Math.abs(n * 100 - Math.round(n * 100)) > 1e-6;
}

type FieldKey =
  | "ticketNo" | "weighedAt" | "fieldCropId" | "unit" | "gross" | "tare" | "net"
  | "moisture" | "testWeight" | "dockage" | "truck" | "driver" | "binId"
  | "form"; // not a control: a refusal that names none (a 404, a dropped connection)

/** Reading order of the form - the alert shows the first problem in this order. */
const FIELD_ORDER: FieldKey[] = [
  "ticketNo", "weighedAt", "fieldCropId", "unit", "gross", "tare", "net",
  "moisture", "testWeight", "dockage", "truck", "driver", "binId", "form",
];

/** The API names a problem by its own key; the form's key for the same control. */
const API_KEY_TO_FIELD: Record<string, FieldKey> = {
  ticketNo: "ticketNo", weighedAt: "weighedAt", fieldCropId: "fieldCropId", unit: "unit", gross: "gross",
  tare: "tare", net: "net", moisture: "moisture", testWeight: "testWeight", dockage: "dockage",
  truck: "truck", driver: "driver", destination: "binId",
};

type Problems = Partial<Record<FieldKey, string>>;

function Field({ id, label, children, className }: { id: string; label: string; children: ReactNode; className?: string }) {
  return (
    <span className={className ? `grain-loads__field ${className}` : "grain-loads__field"}>
      <label htmlFor={id}>{label}</label>
      {children}
    </span>
  );
}

export function GrainLoadDialog({
  bins,
  plantings,
  onClose,
  onSaved,
}: {
  bins: GrainBin[];
  plantings: PlantingOption[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [ticketNo, setTicketNo] = useState("");
  const [weighedAt, setWeighedAt] = useState(localStampNow);
  const [fieldCropId, setFieldCropId] = useState("");
  const [unit, setUnit] = useState<GrainUnit>("bu");
  const [gross, setGross] = useState("");
  const [tare, setTare] = useState("");
  const [moisture, setMoisture] = useState("");
  const [testWeight, setTestWeight] = useState("");
  const [dockage, setDockage] = useState("");
  const [truck, setTruck] = useState("");
  const [driver, setDriver] = useState("");
  const [binId, setBinId] = useState("");
  const [saving, setSaving] = useState(false);
  const [problems, setProblems] = useState<Problems>({});

  const formId = useId();

  const ids = {
    ticketNo: useId(), weighedAt: useId(), fieldCropId: useId(), unit: useId(), gross: useId(), tare: useId(),
    net: useId(), moisture: useId(), testWeight: useId(), dockage: useId(), truck: useId(), driver: useId(),
    binId: useId(),
  };
  const noPlantingsId = useId();
  const alertId = useId();

  // Derived, never stored: the net is gross - tare to the cent, shown read-only and sent as typed by this rule.
  const grossN = toNumber(gross);
  const tareN = toNumber(tare);
  const netN = grossN !== null && tareN !== null ? Math.round((grossN - tareN) * 100) / 100 : null;

  const firstProblemKey = FIELD_ORDER.find((k) => problems[k] !== undefined);
  const message = firstProblemKey ? problems[firstProblemKey] : null;
  const invalid = (k: FieldKey) => (problems[k] !== undefined ? true : undefined);
  const describedBy = (k: FieldKey) => (problems[k] !== undefined ? alertId : undefined);

  /** @ui-check: client-side ONLY for what is cheap to say before sending (required, tare below gross, ranges). The
   *  duplicate-ticket and unit-vs-bin refusals are the API's alone and are shown from its response. */
  function validate(): Problems {
    const p: Problems = {};
    if (ticketNo.trim() === "") p.ticketNo = "Enter the ticket number.";
    else if ([...ticketNo.trim()].length > 40) p.ticketNo = "The ticket number can be at most 40 characters.";

    const when = new Date(weighedAt);
    if (weighedAt === "" || Number.isNaN(when.getTime())) p.weighedAt = "Enter when the load was weighed.";
    else if (when.getTime() > Date.now() + 24 * 3_600_000) p.weighedAt = "A load cannot be weighed in the future.";

    if (fieldCropId === "") p.fieldCropId = "Choose the field and crop the load came off.";

    if (grossN === null || grossN <= 0) p.gross = "Gross weight must be greater than zero.";
    else if (hasMoreThanTwoDecimals(grossN)) p.gross = "Gross weight can have at most 2 decimals.";

    if (tareN === null || tareN < 0) p.tare = "Tare must be zero or more.";
    else if (hasMoreThanTwoDecimals(tareN)) p.tare = "Tare can have at most 2 decimals.";
    else if (grossN !== null && grossN > 0 && tareN >= grossN) p.tare = "Tare must be less than the gross weight.";

    for (const [key, raw, name] of [["moisture", moisture, "Moisture"], ["dockage", dockage, "Dockage"]] as const) {
      const n = toNumber(raw);
      if (raw.trim() !== "" && (n === null || n < 0 || n > 100 || hasMoreThanTwoDecimals(n))) {
        p[key] = `${name} must be between 0 and 100, with at most 2 decimals.`;
      }
    }
    const tw = toNumber(testWeight);
    if (testWeight.trim() !== "" && (tw === null || tw <= 0 || hasMoreThanTwoDecimals(tw))) {
      p.testWeight = "Test weight must be greater than zero, with at most 2 decimals.";
    }
    if ([...truck.trim()].length > 40) p.truck = "Truck can be at most 40 characters.";
    if ([...driver.trim()].length > 80) p.driver = "Driver can be at most 80 characters.";

    if (binId === "") p.binId = "Choose the bin the load goes into.";
    return p;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (saving) return;
    const found = validate();
    setProblems(found);
    if (Object.keys(found).length > 0) return;

    const body: CreateGrainLoadRequest = {
      ticketNo: ticketNo.trim(),
      weighedAt: new Date(weighedAt).toISOString(),
      fieldCropId,
      unit,
      gross: grossN!,
      tare: tareN!,
      net: netN!,
      binId,
    };
    const m = toNumber(moisture);
    if (m !== null) body.moisture = m;
    const t = toNumber(testWeight);
    if (t !== null) body.testWeight = t;
    const d = toNumber(dockage);
    if (d !== null) body.dockage = d;
    if (truck.trim() !== "") body.truck = truck.trim();
    if (driver.trim() !== "") body.driver = driver.trim();

    setSaving(true);
    try {
      await demoApi.createGrainLoad(body);
      onSaved();
    } catch (err) {
      setProblems(refusal(err));
      setSaving(false);
    }
  }

  return (
    <DialogShell
      title="Record a load"
      size="lg"
      onClose={onClose}
      busy={saving}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" form={formId} className="btn btn--primary" disabled={saving}>
            {saving ? "Saving…" : "Save load"}
          </button>
        </>
      }
    >
      <form
        id={formId}
        className="grain-loads__form"
        data-state={saving ? "saving" : message ? "error" : "ready"}
        aria-busy={saving}
        onSubmit={(e) => void handleSubmit(e)}
        noValidate
      >
        <div className="grain-loads__grid">
          <Field id={ids.ticketNo} label="Ticket number">
            <input id={ids.ticketNo} type="text" value={ticketNo} maxLength={40} autoComplete="off"
              aria-required="true" aria-invalid={invalid("ticketNo")} aria-describedby={describedBy("ticketNo")}
              onChange={(e) => setTicketNo(e.target.value)} />
          </Field>
          <Field id={ids.weighedAt} label="Weighed at">
            <input id={ids.weighedAt} type="datetime-local" value={weighedAt}
              aria-required="true" aria-invalid={invalid("weighedAt")} aria-describedby={describedBy("weighedAt")}
              onChange={(e) => setWeighedAt(e.target.value)} />
          </Field>
          <Field id={ids.fieldCropId} label="Field and crop" className="grain-loads__field--wide">
            <select id={ids.fieldCropId} value={fieldCropId}
              aria-required="true" aria-invalid={invalid("fieldCropId")}
              aria-describedby={[describedBy("fieldCropId"), plantings.length === 0 ? noPlantingsId : undefined].filter(Boolean).join(" ") || undefined}
              onChange={(e) => setFieldCropId(e.target.value)}>
              <option value="">Select a field and crop…</option>
              {plantings.map((p) => (
                <option key={p.id} value={p.id}>{p.label}</option>
              ))}
            </select>
            {plantings.length === 0 && (
              <span id={noPlantingsId} className="grain-loads__hint">
                No crop is planted this year. Plant a field on the Fields screen first.
              </span>
            )}
          </Field>
          <Field id={ids.unit} label="Unit">
            <select id={ids.unit} value={unit} aria-required="true" aria-invalid={invalid("unit")}
              aria-describedby={describedBy("unit")} onChange={(e) => setUnit(e.target.value as GrainUnit)}>
              {GRAIN_UNIT_OPTIONS.map((u) => (
                <option key={u.value} value={u.value}>{u.label}</option>
              ))}
            </select>
          </Field>
          <Field id={ids.binId} label="Destination bin">
            <select id={ids.binId} value={binId} aria-required="true" aria-invalid={invalid("binId")}
              aria-describedby={describedBy("binId")} onChange={(e) => setBinId(e.target.value)}>
              <option value="">Select a bin…</option>
              {bins.map((b) => (
                <option key={b.id} value={b.id}>{grainBinLabel(b)}</option>
              ))}
            </select>
          </Field>
          <Field id={ids.gross} label="Gross">
            <input id={ids.gross} type="number" min="0" step="0.01" inputMode="decimal" value={gross}
              aria-required="true" aria-invalid={invalid("gross")} aria-describedby={describedBy("gross")}
              onChange={(e) => setGross(e.target.value)} />
          </Field>
          <Field id={ids.tare} label="Tare">
            <input id={ids.tare} type="number" min="0" step="0.01" inputMode="decimal" value={tare}
              aria-required="true" aria-invalid={invalid("tare")} aria-describedby={describedBy("tare")}
              onChange={(e) => setTare(e.target.value)} />
          </Field>
          <Field id={ids.net} label="Net">
            <input id={ids.net} type="text" readOnly value={netN === null ? "" : netN.toFixed(2)}
              aria-invalid={invalid("net")} aria-describedby={describedBy("net")} />
          </Field>
        </div>

        <fieldset className="grain-loads__readings">
          <legend>Readings (optional)</legend>
          <div className="grain-loads__grid">
            <Field id={ids.moisture} label="Moisture (%)">
              <input id={ids.moisture} type="number" min="0" max="100" step="0.01" inputMode="decimal" value={moisture}
                aria-invalid={invalid("moisture")} aria-describedby={describedBy("moisture")}
                onChange={(e) => setMoisture(e.target.value)} />
            </Field>
            <Field id={ids.testWeight} label="Test weight">
              <input id={ids.testWeight} type="number" min="0" step="0.01" inputMode="decimal" value={testWeight}
                aria-invalid={invalid("testWeight")} aria-describedby={describedBy("testWeight")}
                onChange={(e) => setTestWeight(e.target.value)} />
            </Field>
            <Field id={ids.dockage} label="Dockage (%)">
              <input id={ids.dockage} type="number" min="0" max="100" step="0.01" inputMode="decimal" value={dockage}
                aria-invalid={invalid("dockage")} aria-describedby={describedBy("dockage")}
                onChange={(e) => setDockage(e.target.value)} />
            </Field>
            <Field id={ids.truck} label="Truck">
              <input id={ids.truck} type="text" value={truck} maxLength={40} autoComplete="off"
                aria-invalid={invalid("truck")} aria-describedby={describedBy("truck")}
                onChange={(e) => setTruck(e.target.value)} />
            </Field>
            <Field id={ids.driver} label="Driver" className="grain-loads__field--wide">
              <input id={ids.driver} type="text" value={driver} maxLength={80} autoComplete="off"
                aria-invalid={invalid("driver")} aria-describedby={describedBy("driver")}
                onChange={(e) => setDriver(e.target.value)} />
            </Field>
          </div>
        </fieldset>

        {message && (
          <p id={alertId} className="grain-loads__error" role="alert">
            {message}
          </p>
        )}

      </form>
    </DialogShell>
  );
}

/** The API's own refusal as form problems: a 409 is the duplicate ticket; a 400 names its controls by key. */
function refusal(err: unknown): Problems {
  const text = problemMessage(err, "Could not save this load.");
  if (err instanceof ApiError && err.status === 409) return { ticketNo: text };
  const keyed = problemFieldErrors(err);
  const out: Problems = {};
  for (const [apiKey, messages] of Object.entries(keyed ?? {})) {
    const key = API_KEY_TO_FIELD[apiKey];
    if (key && messages[0]) out[key] = messages[0];
  }
  // Nothing mapped to a control (a 404, a network failure, an unknown key): still say why.
  return Object.keys(out).length > 0 ? out : { form: text };
}
